/**
 * 客户端入口
 * 负责应用挂载和状态恢复（水合）
 *
 * 【核心任务】
 * 1. 创建 Vue 应用实例
 * 2. 从 window.__INITIAL_STATE__ 恢复服务端状态
 * 3. 以水合模式挂载应用
 */
import { createApp } from '@/ssr/create-app';
import { getInitialState } from '@/ssr/serialize';
import ClientOnly from '@/ssr/client-only';
import useSSRInitialStateStore from '@/stores/ssr-state';
import { SSR_CONTEXT_KEY } from '@/ssr/use-context';

// 【1. 创建客户端应用实例】
// createApp(false) 使用 createApp + createWebHistory
const { app, router, pinia } = createApp(false);

// 【2. 注册全局组件】
app.component('ClientOnly', ClientOnly);

// 【3. 获取服务端传递的初始状态】
// HTML 中嵌入: <script>window.__INITIAL_STATE__='...'</script>
const initialState = getInitialState();

// 【4. 创建并注入 SSR 上下文】
// 供 useSSRContext() 使用
const ssrContext = {
  initialState: initialState || { data: {} },
  url: window.location.pathname,
  isClient: true,
};
app.provide(SSR_CONTEXT_KEY, ssrContext);

// 【5. 恢复 Pinia 状态】
// 将服务端状态恢复到 Pinia store
if (initialState && initialState.data) {
  const ssrStore = useSSRInitialStateStore(pinia);
  ssrStore.setData(initialState.data);
}

// 【6. 等待路由就绪并挂载应用】
router.isReady().then(() => {
  /**
   * 【水合模式挂载】
   * app.mount('#app', true)
   * 第二个参数 true 表示水合模式：
   * - 复用服务端渲染的 DOM，而不是重新创建
   * - 保留服务端渲染的内容，绑定事件
   * - 如果不用 true，会导致页面闪烁
   */
  app.mount('#app', true);
});

// 【7. 开发环境热更新】
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    console.log('[Vite HMR] 客户端入口更新');
  });
}

export default app;
